/**
 * The discussion toggle (#1069 Story 1.2, AC 31, C2; UX-DR5, UX-DR6).
 *
 * A `ToolbarActionButton` the SCREEN renders inside `RequestToolbar.actionTrailing`
 * beside Version History and View Devices, on every viewport. Nothing floats
 * over content: the bottom-right pill the launcher used to draw was the FAB the
 * UX decision rejected, drawn with the envelope glyph it forbids. The glyph
 * here is `MessageSquare`; the pattern is exactly `DevicesViewToolbarButton`'s
 * (36px web button, `selected` chrome, accessibility state).
 *
 * `selected` while open, and ALWAYS RENDERED (#1069 Story 5.3).
 *
 * ⚠️ IT USED TO HIDE ITSELF WHILE DOCKED AND OPEN, on the argument that the
 * docked panel carries its own close control and a second affordance beside it
 * is noise. The owner ran that and reversed it on 2026-09-08: a control that
 * disappears exactly when you have most reason to look for it is worse than a
 * redundant one, and the pressed state is what tells you the panel is open.
 * The `docked` prop existed only for that early return and went with it.
 *
 * ⚠️ THE TWO ANDROID REASONS ARE STRUCTURAL, NOT COSMETIC, and were ported from
 * the launcher button they applied to first:
 *
 *  1. ANDROID CLIPS THE BADGE. The button has a `borderRadius`, and Android
 *     clips children that extend past a rounded parent. The dot is therefore a
 *     SIBLING of the button inside this wrapper, which carries NO `borderRadius`
 *     and NO `overflow` — not a child of the `TouchableOpacity`.
 *  2. ANDROID Z-ORDER IS ELEVATION, NOT JSX ORDER. A sibling dot with no
 *     elevation renders BENEATH an elevated button however late it appears in
 *     the tree, so the dot carries `elevation: 6`.
 *
 * ⚠️ THE COMPOSED LABEL, NOT A SEPARATE LABEL ON THE DOT. The button already has
 * a label, so the unread state extends it: "Open discussion, unread". One
 * vocabulary per element: the word is `unread`, never "unread messages" — a
 * plural implies a quantity PRD r8 withdrew. (The space TABS say "New" because
 * that is the word on their badge; this control carries a wordless dot.)
 */
import React from 'react';
import { StyleSheet, View } from 'react-native';

import { UnreadDot } from './UnreadDot';
import { MessageSquare } from '../icons';
import { ToolbarActionButton } from '../shared/ToolbarActionButton';

interface Props {
  open: boolean;
  onToggle: () => void;
  /**
   * FR54: does this request have a thread waiting on this caller? Supplied by
   * the screen from the summary endpoint through `useUnreadSpaceState`.
   */
  hasUnread?: boolean;
  disabled?: boolean;
}

export const DiscussionToggle: React.FC<Props> = ({
  open,
  onToggle,
  hasUnread,
  disabled = false,
}) => {
  return (
    <View style={styles.wrap} testID="correspondence-launcher-wrap">
      <ToolbarActionButton
        label="Discussion"
        icon={MessageSquare}
        onPress={onToggle}
        selected={open}
        expanded={open}
        disabled={disabled}
        testID="correspondence-launcher"
        accessibilityLabel={
          // "Hide", not "Close": the open panel carries its own button named
          // "Close discussion", and since Story 5.3 this toggle is on screen at
          // the same time. Two controls with one accessible name is an
          // ambiguity for voice control; the hint already said "Hide".
          open
            ? 'Hide discussion'
            : hasUnread
            ? 'Open discussion, unread'
            : 'Open discussion'
        }
        accessibilityHint={
          open
            ? 'Hide the discussion for this request'
            : 'Show the discussion for this request'
        }
      />
      {/* Nothing at all when there is nothing unread — see `UnreadDot`. */}
      <UnreadDot
        unread={!open && hasUnread}
        style={styles.dot}
        testID="correspondence-launcher-unread"
      />
    </View>
  );
};

const styles = StyleSheet.create({
  /**
   * The wrapper the button and its dot share. It carries NO `borderRadius` and
   * NO `overflow` — see the header for why that is what stops Android clipping
   * the badge.
   */
  wrap: {
    position: 'relative',
    alignSelf: 'center',
  },
  /**
   * Top-right of the button, half-overhanging it, where users already expect
   * an unread badge. `elevation: 6` beats the button's; on Android that is the
   * ONLY thing deciding which of two siblings paints on top.
   */
  dot: {
    position: 'absolute',
    top: -2,
    right: -2,
    elevation: 6,
    zIndex: 1,
  },
});
